import { createSlice, PayloadAction } from '@reduxjs/toolkit';

/**
 * User preferences for the voice-note transcription pipeline.
 *
 * The transcription pipeline (offscreen Whisper — see
 * `src/offscreen.ts` `doTranscribeInner`) normally runs a two-pass
 * strategy: first an extra inference step to detect the source
 * language, then the actual transcription with that hint. The detect
 * pass adds noticeable latency (a few hundred ms on modern hardware,
 * more on cold model load) and can misfire on very short clips
 * (single word, background noise), leaving Whisper's silent English
 * fallback in charge.
 *
 * When the user knows the language their contacts speak (which is
 * usually the case), letting them lock it in via a picker skips that
 * detect pass entirely. The pipeline uses the saved code directly.
 * `'auto'` restores the original detect-first behaviour.
 *
 * Storage: localStorage under `bt:transcription-prefs`. Chose a
 * dedicated key rather than mixing into DisplaySettings because the
 * concerns don't overlap — DisplaySettings tracks toggles that hide
 * WA-native UI, this reducer tracks a knob for OUR transcription
 * pipeline. Separate keys also mean a reset of one doesn't touch the
 * other.
 */

const STORAGE_KEY = 'bt:transcription-prefs';

/** Sentinel value that maps to "run the detect pass first". Kept as
 *  a distinct string (not `null`) so the picker can render it as a
 *  first-class option alongside the concrete language codes. */
export const AUTO_LANG = 'auto';

export type TranscriptionSourceLang = string;

export interface TranscriptionPrefsState {
  sourceLanguage: TranscriptionSourceLang;
}

const defaults: TranscriptionPrefsState = {
  sourceLanguage: AUTO_LANG,
};

const hydrate = (): TranscriptionPrefsState => {
  try {
    const raw =
      typeof localStorage !== 'undefined'
        ? localStorage.getItem(STORAGE_KEY)
        : null;
    if (!raw) return { ...defaults };
    const parsed = JSON.parse(raw) as Partial<TranscriptionPrefsState>;
    return { ...defaults, ...parsed };
  } catch {
    return { ...defaults };
  }
};

/** Extra sync-key used by the offscreen document. localStorage is
 *  per-origin and offscreen runs at a different origin, so the pref
 *  wouldn't reach it otherwise. `chrome.storage.local` is shared
 *  across every extension context — the offscreen transcription
 *  handler reads it inline before each Whisper run to decide whether
 *  to skip the language-detect pass. */
const CHROME_STORAGE_KEY = 'bt-transcribe-source-lang';

const persist = (state: TranscriptionPrefsState) => {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    }
  } catch (err) {
    console.warn('[transcription-prefs] persist failed', err);
  }
  // Mirror to chrome.storage.local so the offscreen document can read
  // it. The check for `chrome.storage` covers the common/web build
  // where the Chrome extension APIs are undefined.
  try {
    const g = globalThis as unknown as {
      chrome?: { storage?: { local?: { set: (obj: unknown) => Promise<void> } } };
    };
    const local = g.chrome?.storage?.local;
    if (local && typeof local.set === 'function') {
      const write = local.set({ [CHROME_STORAGE_KEY]: state.sourceLanguage });
      // Await via `.then` so a promise rejection here doesn't sink
      // the whole reducer, but a failed write is logged loud enough
      // to see in the offscreen doc's diagnostic output.
      Promise.resolve(write).then(
        () => console.debug(`[transcription-prefs] chrome.storage.local.set OK: ${state.sourceLanguage}`),
        (err) => console.warn('[transcription-prefs] chrome.storage.local.set REJECTED', err),
      );
    } else {
      // Expected in the web build, where Chrome extension APIs don't exist.
      console.debug(
        '[transcription-prefs] chrome.storage.local unavailable',
        { hasChrome: typeof g.chrome !== 'undefined', hasStorage: !!g.chrome?.storage },
      );
    }
  } catch (err) {
    console.warn('[transcription-prefs] chrome.storage sync threw', err);
  }
};

const slice = createSlice({
  name: 'TranscriptionPrefs',
  initialState: hydrate(),
  reducers: {
    setTranscriptionSourceLanguage(
      state,
      action: PayloadAction<TranscriptionSourceLang>,
    ) {
      state.sourceLanguage = action.payload || AUTO_LANG;
      persist(state);
    },
    resetTranscriptionPrefs(state) {
      state.sourceLanguage = AUTO_LANG;
      persist(state);
    },
  },
});

export const {
  setTranscriptionSourceLanguage,
  resetTranscriptionPrefs,
} = slice.actions;

// ─── One-shot sync at module import ─────────────────────────────────
//
// The reducer's `persist` only runs on dispatched actions — an inherited
// value from localStorage (a user who picked Hebrew in a previous
// session) never makes it into chrome.storage.local, which is where the
// offscreen document reads from. That was the whole reason the picker
// showed Hebrew but transcriptions still ran on the generic
// whisper-medium model.
//
// Firing persist(hydrate()) at import time closes that gap: every
// content-script boot (which is what pulls this file in) mirrors the
// persisted state into chrome.storage.local, so by the time the user
// clicks Transcribe the shared extension storage is already correct
// regardless of whether they've opened Display Settings this session.
try {
  persist(hydrate());
} catch (err) {
  console.warn('[transcription-prefs] init-time sync failed', err);
}

export default slice.reducer;
